import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  User as UserIcon,
  Mail,
  Briefcase,
  Shield,
  Trash2,
  Users,
  UserPlus,
} from "lucide-react";
import { LoginUserData } from "../LoginForm/LoginForm";
import { mockAPI } from "../../services/mockAPI";
import { realAPI } from "../../services/realAPI";

interface UserManagementProps {
  currentUser: LoginUserData;
  onStatsUpdate: () => void;
  onGoToQuotas?: () => void; // optional callback to navigate to Quotas & Balance
}

interface User {
  id: string;
  organizationId: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  role: "ADMIN" | "MEMBER";
  teamIds: string[];
  createdAt: Date;
}

interface Team {
  id: string;
  name: string;
  memberIds: string[];
}

const UserManagement: React.FC<UserManagementProps> = ({
  currentUser,
  onStatsUpdate,
  onGoToQuotas,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    jobTitle: "",
    role: "MEMBER" as "ADMIN" | "MEMBER",
    password: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [canAddUsers, setCanAddUsers] = useState<boolean>(true);
  const [userQuotaInfo, setUserQuotaInfo] = useState<{
    balance: number;
    quota: number;
  } | null>(null);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [usersData, teamsData, orgInfo] = await Promise.all([
        realAPI.getUsersByOrganization(currentUser.organizationId),
        realAPI.getTeamsByOrganization(currentUser.organizationId),
        realAPI.getCurrentOrganization(),
      ]);
      setUsers(usersData);
      setTeams(teamsData);

      // Determine USERS quota balance from organization data
      try {
        const quotas = orgInfo?.progressivePlanQuotas || [];
        const userQuota = quotas.find(
          (q: any) => q.progressivePricingType === "USERS"
        );
        if (
          userQuota &&
          typeof userQuota.balance === "number" &&
          typeof userQuota.quota === "number"
        ) {
          setUserQuotaInfo({
            balance: userQuota.balance,
            quota: userQuota.quota,
          });
          setCanAddUsers(userQuota.balance > 0);
        } else {
          // If no USERS quota found, default to allow adding users
          setUserQuotaInfo(null);
          setCanAddUsers(true);
        }
      } catch (e) {
        console.warn("Failed to compute USERS quota from org info", e);
        setUserQuotaInfo(null);
        setCanAddUsers(true);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser.organizationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.jobTitle.trim()) newErrors.jobTitle = "Job title is required";
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent adding new users if USERS quota balance is zero
    if (!canAddUsers) {
      setErrors({
        general:
          "No remaining user credits. Please top up your USERS quota in Quotas & Balance.",
      });
      return;
    }

    if (!validateForm()) return;

    try {
      await realAPI.createUser({
        organizationId: currentUser.organizationId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        jobTitle: formData.jobTitle,
        role: formData.role,
        password: formData.password,
        createdBy: currentUser.id,
      });

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        jobTitle: "",
        role: "MEMBER",
        password: "",
      });
      setShowCreateForm(false);
      await loadData();
      onStatsUpdate();
    } catch (error) {
      console.error("Error creating user:", error);
      const msg = error instanceof Error ? error.message : String(error);
      if (/insufficient balance for users/i.test(msg)) {
        setErrors({
          general:
            "No remaining user credits. Please top up your USERS quota in Quotas & Balance.",
        });
        setCanAddUsers(false);
        setUserQuotaInfo((prev) =>
          prev ? { ...prev, balance: 0 } : { balance: 0, quota: 0 }
        );
      } else {
        setErrors({ general: msg });
      }
    }
  };

  const getUserTeams = (user: User) => {
    return teams.filter((team) => user.teamIds.includes(team.id));
  };

  const openAssignModal = (user: User) => {
    setSelectedUser(user);
    // Get teams that the user is not already a member of
    const userTeamIds = user.teamIds;
    const available = teams.filter((team) => !userTeamIds.includes(team.id));
    setAvailableTeams(available);
    setShowAssignModal(true);
  };

  const handleAssignToTeam = async (teamId: string) => {
    if (!selectedUser) return;

    try {
      await mockAPI.addUserToTeam(teamId, selectedUser.id);
      await loadData();
      onStatsUpdate();
      setShowAssignModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error("Error assigning user to team:", error);
    }
  };

  const handleRemoveFromTeam = async (user: User, teamId: string) => {
    try {
      await mockAPI.removeUserFromTeam(teamId, user.id);
      await loadData();
      onStatsUpdate();
    } catch (error) {
      console.error("Error removing user from team:", error);
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
        <div className="flex items-center space-x-3">
          {userQuotaInfo && userQuotaInfo.balance <= 0 && (
            <div className="text-sm text-amber-700 bg-amber-100 border border-amber-200 rounded px-3 py-1 flex items-center space-x-2">
              <span>USERS quota exhausted. Top up to add users.</span>
              <button
                type="button"
                onClick={() => onGoToQuotas && onGoToQuotas()}
                className="text-amber-800 underline hover:text-amber-900"
              >
                Top up now
              </button>
            </div>
          )}
          <button
            onClick={() => setShowCreateForm(true)}
            disabled={!canAddUsers}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
              canAddUsers
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-600 cursor-not-allowed"
            }`}
            title={
              canAddUsers
                ? "Add a new user"
                : "No remaining user credits. Please top up your USERS quota."
            }
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </button>
        </div>
      </div>

      {/* USERS Quota Summary */}
      {userQuotaInfo && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between text-blue-900 text-sm">
            <span>
              You have {userQuotaInfo.balance} users left out of{" "}
              {userQuotaInfo.quota} users
            </span>
            <button
              type="button"
              onClick={() => onGoToQuotas && onGoToQuotas()}
              className="ml-4 inline-flex items-center px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
            >
              Top up now
            </button>
          </div>
        </div>
      )}

      {/* Create User Form */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Add New User
            </h3>

            {errors.general && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{errors.general}</p>
              </div>
            )}

            <form onSubmit={handleCreateUser}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) =>
                        setFormData({ ...formData, firstName: e.target.value })
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.firstName ? "border-red-300" : "border-gray-300"
                      }`}
                      placeholder="John"
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) =>
                        setFormData({ ...formData, lastName: e.target.value })
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.lastName ? "border-red-300" : "border-gray-300"
                      }`}
                      placeholder="Doe"
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.email ? "border-red-300" : "border-gray-300"
                    }`}
                    placeholder="john.doe@company.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    value={formData.jobTitle}
                    onChange={(e) =>
                      setFormData({ ...formData, jobTitle: e.target.value })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.jobTitle ? "border-red-300" : "border-gray-300"
                    }`}
                    placeholder="Software Engineer"
                  />
                  {errors.jobTitle && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.jobTitle}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        role: e.target.value as "ADMIN" | "MEMBER",
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="MEMBER">MEMBER</option>
                    <option value="ADMIN">ADMIN</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Temporary Password *
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.password ? "border-red-300" : "border-gray-300"
                    }`}
                    placeholder="Minimum 8 characters"
                  />
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.password}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    User should change this password on first login
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setErrors({});
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign User to Team Modal */}
      {showAssignModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Assign {selectedUser.firstName} {selectedUser.lastName} to Team
            </h3>

            {availableTeams.length === 0 ? (
              <div className="text-center py-6">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No available teams
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  This user is already a member of all existing teams.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {availableTeams.map((team) => (
                  <div
                    key={team.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                  >
                    <div>
                      <h4 className="font-medium text-gray-900">{team.name}</h4>
                      <p className="text-sm text-gray-500">
                        {team.memberIds.length} members
                      </p>
                    </div>
                    <button
                      onClick={() => handleAssignToTeam(team.id)}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                    >
                      Assign
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {users.map((user) => (
            <li key={user.id}>
              <div className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-gray-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <p className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </p>
                      {user.role === "ADMIN" && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Shield className="w-3 h-3 mr-1" />
                          Admin
                        </span>
                      )}
                    </div>
                    <div className="flex items-center mt-1 text-sm text-gray-500">
                      <Mail className="w-4 h-4 mr-1" />
                      {user.email}
                    </div>
                    <div className="flex items-center mt-1 text-sm text-gray-500">
                      <Briefcase className="w-4 h-4 mr-1" />
                      {user.jobTitle}
                    </div>
                    {user.teamIds.length > 0 && (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-1">
                          {getUserTeams(user).map((team) => (
                            <span
                              key={team.id}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {team.name}
                              <button
                                onClick={() =>
                                  handleRemoveFromTeam(user, team.id)
                                }
                                className="ml-1 text-gray-500 hover:text-red-600"
                                title="Remove from team"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">
                    Joined {formatDate(user.createdAt)}
                  </span>
                  <button
                    onClick={() => openAssignModal(user)}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    title="Assign to team"
                  >
                    <UserPlus className="w-4 h-4" />
                  </button>
                  {user.id !== currentUser.id && (
                    <button
                      onClick={async () => {
                        if (user.id === currentUser.id) {
                          alert("You cannot delete your own account.");
                          return;
                        }
                        const sure = window.confirm(
                          `Delete user ${user.firstName} ${user.lastName}? This action cannot be undone.`
                        );
                        if (!sure) return;
                        try {
                          const resp = await realAPI.users.deleteUser(user.id);
                          const ok = resp?.success !== false;
                          const msg =
                            resp?.message ||
                            (ok
                              ? "User deleted successfully."
                              : "Failed to delete user.");
                          if (!ok) {
                            alert(msg);
                            return;
                          }
                          // Refresh users list and stats
                          await loadData();
                          onStatsUpdate();
                        } catch (e: any) {
                          const errMsg = e?.message || "Failed to delete user.";
                          alert(errMsg);
                        }
                      }}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Delete user"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>

        {users.length === 0 && (
          <div className="text-center py-12">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding a new user.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add User
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
